import { useEffect, useState } from 'react';
import {
  ArrowRight,
  X,
  Check,
  Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  useAcceptProposal,
  useDiscardProposal,
  useUndoAcceptAll,
} from '@/hooks/use-compliance-dashboard';
import { toast } from '@/app/components/ui/simple-toaster';
import type {
  AssessmentProposal,
  AssessmentProposalChange,
} from '@/lib/types';
import { StatusBadge } from '@/app/pages/compliance/components/compliance-status';

const STATUS_RANK: Record<string, number> = {
  non_compliant: 0,
  not_assessed: 1,
  partially_compliant: 2,
  compliant: 3,
  not_applicable: -1,
};

export function directionLabel(change: AssessmentProposalChange): { text: string; color: string } {
  // Any move to "not_assessed" discards an existing determination (see
  // isSafeToPreSelect). Label it a Downgrade regardless of numeric rank so the
  // UI matches the fact that it is never pre-selected — e.g. not_applicable
  // (rank -1) → not_assessed (rank 1) must not read as a green "Upgrade".
  if (change.new_status === 'not_assessed') return { text: 'Downgrade', color: 'text-red-600' };
  const oldRank = STATUS_RANK[change.old_status] ?? 1;
  const newRank = STATUS_RANK[change.new_status] ?? 1;
  if (newRank > oldRank) return { text: 'Upgrade', color: 'text-green-600' };
  if (newRank < oldRank) return { text: 'Downgrade', color: 'text-red-600' };
  return { text: 'Change', color: 'text-gray-600' };
}

export function isSafeToPreSelect(change: AssessmentProposalChange): boolean {
  // Any move TO "not_assessed" reverts a previously determined clause
  // (compliant, non_compliant, or not_applicable) back to "unknown". That can
  // never be an evidence-based conclusion — whatever phrasing the engine emits
  // ("Requires manual assessment.", "No incident history to assess.", or any
  // future variant) — so it must never be silently pre-checked under "Accept
  // All"; it always needs explicit per-item confirmation before it can discard
  // an existing determination. This is the only unsafe case: every other
  // transition (upgrades, lateral moves, and evidence-based downgrades to
  // non_compliant / partially_compliant) is a legitimate engine conclusion and
  // stays pre-selectable.
  return change.new_status !== 'not_assessed';
}

export function ProposalReviewPanel({ proposal }: { proposal: AssessmentProposal }) {
  const changes = proposal.proposed_changes;
  const [acceptAllConfirmation, setAcceptAllConfirmation] = useState<{
    proposalId: number;
    changeSignature: string;
  } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(changes.filter(isSafeToPreSelect).map((c) => c.clause_ref)),
  );

  // Key the reset on the actual change content, not just the count: a new
  // proposal can reuse an id and length while swapping clauses/statuses, and
  // keying on length alone would leave a stale selection that could keep a
  // destructive downgrade pre-checked.
  const changeSignature = changes
    .map((c) => `${c.clause_ref}:${c.old_status}->${c.new_status}`)
    .join('|');
  useEffect(() => {
    setSelected(new Set(changes.filter(isSafeToPreSelect).map((c) => c.clause_ref)));
    setAcceptAllConfirmation(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.id, changeSignature]);
  const acceptMutation = useAcceptProposal();
  const discardMutation = useDiscardProposal();
  const undoMutation = useUndoAcceptAll();

  const allSelected = selected.size === changes.length;
  const noneSelected = selected.size === 0;

  // "Select all" must never sweep in a destructive downgrade. Only changes that
  // are safe to pre-select participate in the bulk toggle; any move to
  // not_assessed is excluded so it can only ever be applied via an explicit
  // per-item click — never in one bulk "Accept All" action.
  const selectableRefs = changes.filter(isSafeToPreSelect).map((c) => c.clause_ref);
  const allSelectableSelected =
    selectableRefs.length > 0 && selectableRefs.every((ref) => selected.has(ref));
  const allSafeAndSelected = allSelected && selectableRefs.length === changes.length;

  function toggleAll() {
    setSelected(allSelectableSelected ? new Set() : new Set(selectableRefs));
  }

  function toggle(ref: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) {
        next.delete(ref);
      } else {
        next.add(ref);
      }
      return next;
    });
  }

  function handleAccept() {
    if (allSafeAndSelected) {
      setAcceptAllConfirmation({
        proposalId: proposal.id,
        changeSignature,
      });
      return;
    }

    acceptMutation.mutate({
      proposalId: proposal.id,
      clauseRefs: Array.from(selected),
    });
  }

  function handleAcceptAll() {
    if (!acceptAllConfirmation) return;

    acceptMutation.mutate({
      proposalId: acceptAllConfirmation.proposalId,
      clauseRefs: null,
    }, {
      onSuccess: (result) => {
        if (!result.undo_batch) return;
        const batchId = result.undo_batch.id;
        toast.success('Compliance register updated', {
          description: 'You can undo this Accept All for seven days.',
          duration: 15000,
          action: {
            label: 'Undo',
            onClick: () => {
              undoMutation.mutate(batchId, {
                onSuccess: (undoResult) => {
                  if (undoResult.skipped_clauses.length > 0) {
                    toast.warning('Undo completed with skipped items', {
                      description: `Later edits were kept for: ${undoResult.skipped_clauses.join(', ')}`,
                    });
                  } else {
                    toast.success('Accept All undone');
                  }
                },
              });
            },
          },
        });
      },
    });
    setAcceptAllConfirmation(null);
  }

  function handleDiscard() {
    discardMutation.mutate(proposal.id);
  }

  const isBusy = acceptMutation.isPending || discardMutation.isPending || undoMutation.isPending;
  const { summary } = proposal;

  if (changes.length === 0) {
    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-blue-900">Assessment Complete</h3>
              <p className="mt-1 text-sm text-blue-700">
                No status changes detected — all {summary.applicable ?? summary.total_assessed} applicable clauses are up to date.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={isBusy}
            >
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200">
      <CardContent className="pt-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Assessment Proposal</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              {summary.total_changes} change{summary.total_changes !== 1 ? 's' : ''} proposed
              {summary.upgrades > 0 && (
                <span className="ml-2 text-green-600">{summary.upgrades} upgrade{summary.upgrades !== 1 ? 's' : ''}</span>
              )}
              {summary.downgrades > 0 && (
                <span className="ml-2 text-red-600">{summary.downgrades} downgrade{summary.downgrades !== 1 ? 's' : ''}</span>
              )}
              {' '}&middot; {summary.unchanged} unchanged
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={isBusy}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              disabled={noneSelected || isBusy}
            >
              <Check className="mr-1.5 h-3.5 w-3.5" />
              {allSafeAndSelected
                ? 'Accept All'
                : `Accept ${selected.size} Clause${selected.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>

        {/* Select-all toggle */}
        <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
          <Checkbox
            checked={allSelectableSelected}
            onCheckedChange={toggleAll}
            disabled={isBusy}
          />
          <span className="text-xs font-medium text-gray-500">
            {allSelectableSelected ? 'Deselect all' : 'Select all'}
          </span>
        </div>

        {/* Clause change rows */}
        <div className="space-y-1">
          {changes.map((change) => {
            const dir = directionLabel(change);
            return (
              <label
                key={change.clause_ref}
                className="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50"
              >
                <Checkbox
                  checked={selected.has(change.clause_ref)}
                  onCheckedChange={() => toggle(change.clause_ref)}
                  disabled={isBusy}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      {change.clause_ref}
                    </span>
                    <span className="truncate text-sm text-gray-500">
                      {change.clause_title}
                    </span>
                    <span className={`text-xs font-medium ${dir.color}`}>
                      {dir.text}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <StatusBadge status={change.old_status} />
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <StatusBadge status={change.new_status} />
                  </div>
                  {change.evidence_notes && (
                    <p className="mt-1 text-xs text-gray-500">{change.evidence_notes}</p>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer actions (mobile-friendly duplicate) */}
        <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDiscard}
            disabled={isBusy}
          >
            <X className="mr-1.5 h-3.5 w-3.5" />
            Discard All
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={noneSelected || isBusy}
          >
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {allSafeAndSelected
              ? 'Accept All'
              : `Accept Selected (${selected.size})`}
          </Button>
        </div>
      </CardContent>
      <AlertDialog
        open={
          acceptAllConfirmation?.proposalId === proposal.id
          && acceptAllConfirmation.changeSignature === changeSignature
        }
        onOpenChange={(open) => {
          setAcceptAllConfirmation(open ? { proposalId: proposal.id, changeSignature } : null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept all suggested items?</AlertDialogTitle>
            <AlertDialogDescription>
              This will accept all suggested items and replace your current compliance register.
              You can undo it from the register for seven days. Later edits will never be overwritten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcceptAll}>
              Yes, accept all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
